// src/components/Admin/AdminPanel.jsx
// Panel de operación. Nav + vistas: Resumen, Trades/Escrows, Disputas (case view),
// Invitaciones, Usuarios. Enfoque funcional: la evidencia para decidir disputas
// (trade + chat + comprobante + reputación de ambos) bien a la vista.
import { useEffect, useState, useCallback } from 'react';
import { adminFetch } from '../../services/adminClient';

const NAV = [
  { key: 'overview',  label: 'Resumen' },
  { key: 'trades',    label: 'Trades / Escrows' },
  { key: 'disputes',  label: 'Disputas' },
  { key: 'kyc',       label: 'KYC' },
  { key: 'offers',    label: 'Ofertas' },
  { key: 'invites',   label: 'Invitaciones' },
  { key: 'users',     label: 'Usuarios' },
  { key: 'bonds',     label: 'Bonos' },
  { key: 'broadcast', label: 'Difusión' },
  { key: 'activity',  label: 'Actividad' },
];

const BOND_ST = { ACTIVE: 'activo', SLASHED: 'slasheado', RELEASED: 'liberado' };
const KYC_ST  = { PENDING: 'pendiente', IN_REVIEW: 'en revisión', APPROVED: 'aprobado', REJECTED: 'rechazado', EXPIRED: 'expirado' };
const ACTION_LABELS = {
  resolve_dispute: 'Resolvió disputa', slash_bond: 'Slasheó bono', release_bond: 'Liberó bono',
  create_bond: 'Registró bono', verify_user: 'Verificó', unverify_user: 'Revocó verificación',
  suspend_user: 'Suspendió', unsuspend_user: 'Reactivó', create_invite: 'Creó código', message_user: 'Mensaje a usuario',
  broadcast: 'Difusión', enable_offer: 'Habilitó oferta', disable_offer: 'Bajó oferta',
  approve_kyc: 'Aprobó KYC', reject_kyc: 'Rechazó KYC',
};

const STATUS = {
  CREATED: 'Creado', WAITING_PAYMENT: 'Esperando pago', PAID: 'Pagado',
  COMPLETED: 'Completado', CANCELLED: 'Cancelado', DISPUTED: 'En disputa',
};
const fmtDate = (d) => (d ? new Date(d).toLocaleString('es-CR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const fmtNum  = (n) => Number(n || 0).toLocaleString('es-CR');

export default function AdminPanel({ onLogout }) {
  const [view, setView]       = useState('overview');
  const [caseId, setCaseId]   = useState(null); // tradeId abierto en case view
  const [userId, setUserId]   = useState(null); // userId abierto en vista 360

  return (
    <div className="adm-shell">
      <aside className="adm-side">
        <div className="adm-side-brand">BIT<span>PLIFY</span></div>
        <nav className="adm-nav">
          {NAV.map((n) => (
            <button key={n.key}
              className={`adm-nav-item ${view === n.key && !caseId && !userId ? 'active' : ''}`}
              onClick={() => { setCaseId(null); setUserId(null); setView(n.key); }}>
              {n.label}
            </button>
          ))}
        </nav>
        <button className="adm-btn adm-logout" onClick={onLogout}>Cerrar sesión</button>
      </aside>

      <main className="adm-main">
        {caseId ? (
          <CaseView tradeId={caseId} onBack={() => setCaseId(null)} />
        ) : userId ? (
          <UserDetailView userId={userId} onBack={() => setUserId(null)} onOpenTrade={setCaseId} />
        ) : (
          <>
            {view === 'overview'  && <OverviewView />}
            {view === 'trades'    && <TradesView   onOpen={setCaseId} />}
            {view === 'disputes'  && <DisputesView onOpen={setCaseId} />}
            {view === 'kyc'       && <KycView />}
            {view === 'offers'    && <OffersView />}
            {view === 'invites'   && <InvitesView />}
            {view === 'users'     && <UsersView onOpen={setUserId} />}
            {view === 'bonds'     && <BondsView />}
            {view === 'broadcast' && <BroadcastView />}
            {view === 'activity'  && <ActivityView />}
          </>
        )}
      </main>
    </div>
  );
}

// ── hook de carga simple ──────────────────────────────────────────────
function useData(path, deps = []) {
  const [data, setData]   = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoad] = useState(true);
  const reload = useCallback(async () => {
    setLoad(true); setError('');
    try {
      const res = await adminFetch(path);
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.message || 'Error');
      setData(json);
    } catch (e) { setError(e.message || 'Error'); }
    finally { setLoad(false); }
  }, [path]); // eslint-disable-line
  useEffect(() => { reload(); }, deps); // eslint-disable-line
  return { data, error, loading, reload };
}

// ── RESUMEN ───────────────────────────────────────────────────────────
function OverviewView() {
  const { data, error, loading } = useData('/api/admin/overview');
  if (loading) return <Loading />;
  if (error)   return <ErrorBox msg={error} />;
  const cards = [
    ['Usuarios', data.users],
    ['Vendedores verificados', data.verifiedSellers],
    ['Disputas abiertas', data.disputesOpen, data.disputesOpen > 0 ? 'warn' : ''],
    ['Escrows activos', data.escrowsActive],
    ['Invites sin usar', data.invitesUnused],
    ['Trades totales', data.tradesTotal],
  ];
  return (
    <div>
      <h1 className="adm-title">Resumen</h1>
      <div className="adm-cards">
        {cards.map(([label, val, cls]) => (
          <div key={label} className={`adm-card ${cls || ''}`}>
            <div className="adm-card-val">{fmtNum(val)}</div>
            <div className="adm-card-label">{label}</div>
          </div>
        ))}
      </div>
      <h2 className="adm-subtitle">Trades por estado</h2>
      <div className="adm-chips">
        {Object.entries(data.tradesByStatus || {}).map(([s, c]) => (
          <span key={s} className="adm-chip">{STATUS[s] || s}: <b>{c}</b></span>
        ))}
      </div>
    </div>
  );
}

// ── TRADES / ESCROWS ──────────────────────────────────────────────────
function TradesView({ onOpen }) {
  const [status, setStatus] = useState('');
  const { data, error, loading } = useData(`/api/admin/trades${status ? `?status=${status}` : ''}`, [status]);
  return (
    <div>
      <div className="adm-row-between">
        <h1 className="adm-title">Trades / Escrows</h1>
        <select className="adm-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Todos los estados</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {loading ? <Loading /> : error ? <ErrorBox msg={error} /> : (
        <table className="adm-table">
          <thead><tr>
            <th>ID</th><th>Estado</th><th>Escrow</th><th>Comprador</th><th>Vendedor</th>
            <th>Monto</th><th>Pago</th><th>Fecha</th><th></th>
          </tr></thead>
          <tbody>
            {data.map((t) => (
              <tr key={t.id}>
                <td>T-{t.id}</td>
                <td><span className={`adm-badge st-${t.status}`}>{STATUS[t.status] || t.status}</span></td>
                <td>{t.escrowReleased ? 'liberado' : t.escrowLocked ? 'bloqueado' : '—'}</td>
                <td>@{t.buyer?.username}</td>
                <td>@{t.seller?.username}</td>
                <td className="adm-mono">{fmtNum(t.amount)} {t.crypto}</td>
                <td>{t.payment}</td>
                <td className="adm-muted">{fmtDate(t.createdAt)}</td>
                <td><button className="adm-btn adm-btn-sm" onClick={() => onOpen(t.id)}>Ver</button></td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={9} className="adm-empty">Sin trades</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── DISPUTAS ──────────────────────────────────────────────────────────
function DisputesView({ onOpen }) {
  const { data, error, loading } = useData('/api/admin/disputes');
  if (loading) return <Loading />;
  if (error)   return <ErrorBox msg={error} />;
  return (
    <div>
      <h1 className="adm-title">Disputas</h1>
      <table className="adm-table">
        <thead><tr>
          <th>#</th><th>Trade</th><th>Estado</th><th>Abrió</th><th>Motivo</th><th>Fecha</th><th></th>
        </tr></thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.id} className={d.status === 'OPEN' ? 'adm-row-warn' : ''}>
              <td>#{d.id}</td>
              <td>T-{d.tradeId}</td>
              <td><span className={`adm-badge dp-${d.status}`}>{d.status}</span></td>
              <td>@{d.openedBy?.username}</td>
              <td className="adm-truncate">{d.reason}</td>
              <td className="adm-muted">{fmtDate(d.createdAt)}</td>
              <td><button className="adm-btn adm-btn-sm" onClick={() => onOpen(d.tradeId)}>Revisar</button></td>
            </tr>
          ))}
          {data.length === 0 && <tr><td colSpan={7} className="adm-empty">Sin disputas</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ── CASE VIEW (evidencia + resolución) ────────────────────────────────
function CaseView({ tradeId, onBack }) {
  const { data: t, error, loading, reload } = useData(`/api/admin/trades/${tradeId}`);
  const [note, setNote]     = useState('');
  const [busy, setBusy]     = useState(false);
  const [msg, setMsg]       = useState('');
  const [slashSeller, setSlashSeller] = useState(false);

  const resolve = async (outcome) => {
    if (!t?.dispute) return;
    if (!window.confirm(`Resolver a favor del ${outcome === 'buyer' ? 'COMPRADOR' : 'VENDEDOR'}?`)) return;
    setBusy(true); setMsg('');
    try {
      const res = await adminFetch(`/api/admin/disputes/${t.dispute.id}/resolve`, {
        method: 'PATCH', body: JSON.stringify({ outcome, note, slashSeller: outcome === 'buyer' && slashSeller }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Error');
      setMsg('Disputa resuelta ✓'); reload();
    } catch (e) { setMsg(e.message || 'Error'); }
    finally { setBusy(false); }
  };

  if (loading) return <Loading />;
  if (error)   return <ErrorBox msg={error} />;

  const dp = t.dispute;
  return (
    <div>
      <button className="adm-btn adm-btn-sm adm-back" onClick={onBack}>← Volver</button>
      <h1 className="adm-title">Trade T-{t.id} <span className={`adm-badge st-${t.status}`}>{STATUS[t.status] || t.status}</span></h1>

      <div className="adm-case-grid">
        {/* Datos del trade */}
        <section className="adm-panel">
          <h3>Datos del trade</h3>
          <dl className="adm-dl">
            <dt>Monto</dt><dd className="adm-mono">{fmtNum(t.amount)} {t.crypto}</dd>
            <dt>Precio</dt><dd className="adm-mono">{fmtNum(t.price)}</dd>
            <dt>Red</dt><dd>{t.blockchain}</dd>
            <dt>Pago</dt><dd>{t.payment}</dd>
            <dt>Escrow</dt><dd>{t.escrowReleased ? 'liberado' : t.escrowLocked ? 'bloqueado' : '—'}</dd>
            <dt>Creado</dt><dd className="adm-muted">{fmtDate(t.createdAt)}</dd>
          </dl>
          {t.receiptUrl && (
            <a className="adm-btn adm-btn-sm" href={t.receiptUrl} target="_blank" rel="noopener noreferrer">📎 Ver comprobante</a>
          )}
        </section>

        {/* Partes + reputación */}
        <section className="adm-panel">
          <h3>Partes</h3>
          <Party label="Comprador" u={t.buyer} />
          <Party label="Vendedor"  u={t.seller} />
        </section>

        {/* Chat */}
        <section className="adm-panel adm-chat-panel">
          <h3>Chat ({t.messages?.length || 0})</h3>
          <div className="adm-chat">
            {(t.messages || []).map((m) => (
              <div key={m.id} className="adm-msg">
                <span className="adm-msg-who">@{m.sender?.username}</span>
                <span className="adm-msg-time">{fmtDate(m.createdAt)}</span>
                <div className="adm-msg-body">{m.content}{m.imageUrl && <> <a href={m.imageUrl} target="_blank" rel="noopener noreferrer">[imagen]</a></>}</div>
              </div>
            ))}
            {(!t.messages || t.messages.length === 0) && <div className="adm-empty">Sin mensajes</div>}
          </div>
        </section>
      </div>

      {/* Resolución de disputa */}
      {dp && (
        <section className="adm-panel adm-resolve">
          <h3>Disputa #{dp.id} — <span className={`adm-badge dp-${dp.status}`}>{dp.status}</span></h3>
          <p><b>Abrió:</b> @{dp.openedBy?.username} · <span className="adm-muted">{fmtDate(dp.createdAt)}</span></p>
          <p className="adm-reason"><b>Motivo:</b> {dp.reason}</p>
          {dp.resolution && <p className="adm-muted"><b>Resolución:</b> {dp.resolution}</p>}
          {dp.status === 'OPEN' ? (
            <>
              <textarea className="adm-textarea" placeholder="Nota de la resolución (opcional)"
                value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
              <label className="adm-check">
                <input type="checkbox" checked={slashSeller} onChange={(e) => setSlashSeller(e.target.checked)} />
                Slashear el bono del vendedor (estafa probada) — aplica si gana el comprador
              </label>
              <div className="adm-resolve-actions">
                <button className="adm-btn adm-btn-buyer"  disabled={busy} onClick={() => resolve('buyer')}>
                  A favor del comprador (se completa)
                </button>
                <button className="adm-btn adm-btn-seller" disabled={busy} onClick={() => resolve('seller')}>
                  A favor del vendedor (se cancela)
                </button>
              </div>
            </>
          ) : <p className="adm-muted">Disputa cerrada.</p>}
          {msg && <div className="adm-resolve-msg">{msg}</div>}
        </section>
      )}
    </div>
  );
}

function Party({ label, u }) {
  if (!u) return null;
  return (
    <div className="adm-party">
      <div className="adm-party-head">
        <b>{label}: @{u.username}</b> {u.verified && <span className="adm-badge ok">verificado</span>}
        {u.bonds?.length > 0 && <span className="adm-badge ok">bono {fmtNum(u.bonds.reduce((s, b) => s + b.amount, 0))} {u.bonds[0].currency}</span>}
      </div>
      <div className="adm-party-stats adm-muted">
        ⭐ {Number(u.averageRating || 0).toFixed(1)} ({u.totalReviews || 0}) · {u.completedTrades || 0} completados ·
        {' '}KYC L{u.kycLevel || 0} · {u.country} · desde {fmtDate(u.createdAt)}
      </div>
    </div>
  );
}

// ── INVITACIONES ──────────────────────────────────────────────────────
function InvitesView() {
  const { data, error, loading, reload } = useData('/api/admin/invites');
  const [email, setEmail] = useState('');
  const [busy, setBusy]   = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const res = await adminFetch('/api/admin/invite', {
        method: 'POST', body: JSON.stringify({ email: email.trim() || undefined }),
      });
      if (res.ok) { setEmail(''); reload(); }
    } finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="adm-title">Invitaciones (códigos de vendedor)</h1>
      <div className="adm-inline-form">
        <input className="adm-input" placeholder="email (opcional)" value={email}
          onChange={(e) => setEmail(e.target.value)} />
        <button className="adm-btn adm-btn-primary" onClick={create} disabled={busy}>
          {busy ? '…' : 'Crear código'}
        </button>
      </div>
      {loading ? <Loading /> : error ? <ErrorBox msg={error} /> : (
        <table className="adm-table">
          <thead><tr><th>Código</th><th>Email</th><th>Estado</th><th>Usado por</th><th>Fecha</th><th></th></tr></thead>
          <tbody>
            {data.map((c) => (
              <tr key={c.id}>
                <td className="adm-mono">{c.code}</td>
                <td>{c.email || '—'}</td>
                <td>{c.used ? <span className="adm-badge">usado</span> : <span className="adm-badge ok">libre</span>}</td>
                <td>{c.usedBy ? `#${c.usedBy}` : '—'}</td>
                <td className="adm-muted">{fmtDate(c.createdAt)}</td>
                <td><button className="adm-btn adm-btn-sm" onClick={() => navigator.clipboard?.writeText(c.code)}>Copiar</button></td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={6} className="adm-empty">Sin códigos</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── USUARIOS ──────────────────────────────────────────────────────────
function UsersView({ onOpen }) {
  const [q, setQ]   = useState('');
  const [term, setTerm] = useState('');
  const { data, error, loading, reload } = useData(`/api/admin/users${term ? `?q=${encodeURIComponent(term)}` : ''}`, [term]);

  const act = async (id, path, body) => {
    await adminFetch(`/api/admin/users/${id}/${path}`, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
    reload();
  };
  const message = async (id) => {
    const text = window.prompt('Mensaje para el usuario:');
    if (text && text.trim()) await act(id, 'message', { message: text.trim() });
  };
  const addBond = async (id) => {
    const amount = window.prompt('Monto del bono a registrar (ej. 100):');
    if (amount && parseFloat(amount) > 0) await act(id, 'bond', { amount: parseFloat(amount), currency: 'USDT' });
  };

  return (
    <div>
      <h1 className="adm-title">Usuarios</h1>
      <form className="adm-inline-form" onSubmit={(e) => { e.preventDefault(); setTerm(q.trim()); }}>
        <input className="adm-input" placeholder="buscar por usuario / email / nombre" value={q}
          onChange={(e) => setQ(e.target.value)} />
        <button className="adm-btn adm-btn-primary" type="submit">Buscar</button>
      </form>
      {loading ? <Loading /> : error ? <ErrorBox msg={error} /> : (
        <table className="adm-table">
          <thead><tr>
            <th>Usuario</th><th>Email</th><th>Verif.</th><th>KYC</th><th>Rating</th><th>Trades</th><th>Bono</th><th>Acciones</th>
          </tr></thead>
          <tbody>
            {data.map((u) => (
              <tr key={u.id}>
                <td>
                  <button className="adm-link" onClick={() => onOpen(u.id)}>@{u.username}</button>
                  {u.suspended && <span className="adm-badge st-CANCELLED">susp.</span>}
                  {u.isAdmin && <span className="adm-badge">admin</span>}
                </td>
                <td className="adm-muted">{u.email}</td>
                <td>{u.verified ? <span className="adm-badge ok">sí</span> : 'no'}</td>
                <td>L{u.kycLevel || 0}</td>
                <td className="adm-mono">⭐{Number(u.averageRating || 0).toFixed(1)}</td>
                <td className="adm-mono">{u.trades || 0}</td>
                <td className="adm-mono">{u.bonds?.length ? `${fmtNum(u.bonds.reduce((s, b) => s + b.amount, 0))} ${u.bonds[0].currency}` : '—'}</td>
                <td className="adm-actions">
                  {u.verified
                    ? <button className="adm-btn adm-btn-sm" onClick={() => act(u.id, 'unverify')}>Revocar</button>
                    : <button className="adm-btn adm-btn-sm" onClick={() => act(u.id, 'verify')}>Verificar</button>}
                  <button className="adm-btn adm-btn-sm" onClick={() => message(u.id)}>Mensaje</button>
                  <button className="adm-btn adm-btn-sm" onClick={() => addBond(u.id)}>+Bono</button>
                  {u.suspended
                    ? <button className="adm-btn adm-btn-sm" onClick={() => act(u.id, 'unsuspend')}>Reactivar</button>
                    : <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => { if (window.confirm(`¿Suspender a @${u.username}?`)) act(u.id, 'suspend'); }}>Suspender</button>}
                </td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={8} className="adm-empty">Sin usuarios</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── BONOS de vendedor (Nivel A) ───────────────────────────────────────
function BondsView() {
  const { data, error, loading, reload } = useData('/api/admin/bonds');
  const act = async (id, path) => {
    const note = window.prompt(path === 'slash' ? 'Motivo del slash (opcional):' : 'Nota (opcional):');
    if (note === null) return; // canceló
    await adminFetch(`/api/admin/bonds/${id}/${path}`, {
      method: 'POST', body: note.trim() ? JSON.stringify({ note: note.trim() }) : undefined,
    });
    reload();
  };
  if (loading) return <Loading />;
  if (error)   return <ErrorBox msg={error} />;
  return (
    <div>
      <h1 className="adm-title">Bonos de vendedor</h1>
      <p className="adm-muted adm-hint">Colateral no-custodial (Nivel A). Slashear penaliza al vendedor ante una estafa probada. El bono on-chain real llega con la wallet-infra.</p>
      <table className="adm-table">
        <thead><tr><th>#</th><th>Vendedor</th><th>Monto</th><th>Estado</th><th>Nota</th><th>Fecha</th><th>Acciones</th></tr></thead>
        <tbody>
          {data.map((b) => (
            <tr key={b.id}>
              <td>#{b.id}</td>
              <td>@{b.user?.username}</td>
              <td className="adm-mono">{fmtNum(b.amount)} {b.currency}</td>
              <td><span className={`adm-badge ${b.status === 'ACTIVE' ? 'ok' : b.status === 'SLASHED' ? 'st-CANCELLED' : ''}`}>{BOND_ST[b.status] || b.status}</span></td>
              <td className="adm-truncate">{b.note || '—'}</td>
              <td className="adm-muted">{fmtDate(b.createdAt)}</td>
              <td className="adm-actions">
                {b.status === 'ACTIVE' && (
                  <>
                    <button className="adm-btn adm-btn-sm" onClick={() => act(b.id, 'slash')}>Slashear</button>
                    <button className="adm-btn adm-btn-sm" onClick={() => act(b.id, 'release')}>Liberar</button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {data.length === 0 && <tr><td colSpan={7} className="adm-empty">Sin bonos registrados</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ── KYC (revisión) ────────────────────────────────────────────────────
function KycView() {
  const { data, error, loading, reload } = useData('/api/admin/kyc');
  const review = async (id, decision) => {
    let reason;
    if (decision === 'reject') { reason = window.prompt('Motivo del rechazo:'); if (reason === null) return; }
    await adminFetch(`/api/admin/kyc/${id}`, { method: 'PATCH', body: JSON.stringify({ decision, reason }) });
    reload();
  };
  if (loading) return <Loading />;
  if (error)   return <ErrorBox msg={error} />;
  return (
    <div>
      <h1 className="adm-title">Verificaciones KYC</h1>
      <table className="adm-table">
        <thead><tr><th>#</th><th>Usuario</th><th>Nivel</th><th>Documento</th><th>Estado</th><th>Fecha</th><th>Acciones</th></tr></thead>
        <tbody>
          {data.map((k) => (
            <tr key={k.id} className={['PENDING', 'IN_REVIEW'].includes(k.status) ? 'adm-row-warn' : ''}>
              <td>#{k.id}</td>
              <td>@{k.user?.username} <span className="adm-muted">{k.user?.email}</span></td>
              <td>L{k.level}</td>
              <td>{k.documentType || '—'}</td>
              <td><span className={`adm-badge ${k.status === 'APPROVED' ? 'ok' : ['PENDING', 'IN_REVIEW'].includes(k.status) ? 'st-DISPUTED' : 'st-CANCELLED'}`}>{KYC_ST[k.status] || k.status}</span></td>
              <td className="adm-muted">{fmtDate(k.createdAt)}</td>
              <td className="adm-actions">
                {['PENDING', 'IN_REVIEW'].includes(k.status) && (
                  <>
                    <button className="adm-btn adm-btn-sm adm-btn-seller" onClick={() => review(k.id, 'approve')}>Aprobar</button>
                    <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => review(k.id, 'reject')}>Rechazar</button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {data.length === 0 && <tr><td colSpan={7} className="adm-empty">Sin verificaciones</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ── OFERTAS (moderación) ──────────────────────────────────────────────
function OffersView() {
  const [active, setActive] = useState('');
  const { data, error, loading, reload } = useData(`/api/admin/offers${active ? `?active=${active}` : ''}`, [active]);
  const toggle = async (id, path) => { await adminFetch(`/api/admin/offers/${id}/${path}`, { method: 'POST' }); reload(); };
  return (
    <div>
      <div className="adm-row-between">
        <h1 className="adm-title">Ofertas (moderación)</h1>
        <select className="adm-select" value={active} onChange={(e) => setActive(e.target.value)}>
          <option value="">Todas</option><option value="true">Activas</option><option value="false">Inactivas</option>
        </select>
      </div>
      {loading ? <Loading /> : error ? <ErrorBox msg={error} /> : (
        <table className="adm-table">
          <thead><tr><th>#</th><th>Trader</th><th>Tipo</th><th>Red</th><th>Monto</th><th>Pago</th><th>Estado</th><th>Acciones</th></tr></thead>
          <tbody>
            {data.map((o) => (
              <tr key={o.id}>
                <td>#{o.id}</td>
                <td>@{o.user?.username || o.trader}</td>
                <td>{o.type}</td>
                <td>{o.network}</td>
                <td className="adm-mono">{fmtNum(o.amount)}</td>
                <td>{o.payment}</td>
                <td>{o.active ? <span className="adm-badge ok">activa</span> : <span className="adm-badge st-CANCELLED">inactiva</span>}</td>
                <td className="adm-actions">
                  {o.active ? <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => toggle(o.id, 'disable')}>Bajar</button>
                            : <button className="adm-btn adm-btn-sm" onClick={() => toggle(o.id, 'enable')}>Habilitar</button>}
                </td>
              </tr>
            ))}
            {data.length === 0 && <tr><td colSpan={8} className="adm-empty">Sin ofertas</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── DIFUSIÓN (mensaje masivo) ─────────────────────────────────────────
function BroadcastView() {
  const [title, setTitle]       = useState('');
  const [message, setMessage]   = useState('');
  const [audience, setAudience] = useState('all');
  const [busy, setBusy]         = useState(false);
  const [result, setResult]     = useState('');
  const send = async () => {
    if (!message.trim()) return;
    if (!window.confirm(`¿Enviar a ${audience === 'verified' ? 'vendedores verificados' : 'TODOS los usuarios'}?`)) return;
    setBusy(true); setResult('');
    try {
      const res = await adminFetch('/api/admin/broadcast', { method: 'POST', body: JSON.stringify({ title: title.trim(), message: message.trim(), audience }) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.message || 'Error');
      setResult(`Enviado a ${j.count} usuarios ✓`); setMessage(''); setTitle('');
    } catch (e) { setResult(e.message || 'Error'); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <h1 className="adm-title">Difusión</h1>
      <p className="adm-muted adm-hint">Mensaje masivo (notificación + push) a todos los usuarios o solo a vendedores verificados.</p>
      <div className="adm-panel" style={{ maxWidth: '560px' }}>
        <label className="adm-field"><span>Título (opcional)</span>
          <input className="adm-input" style={{ minWidth: 'auto', width: '100%' }} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} /></label>
        <label className="adm-field"><span>Mensaje</span>
          <textarea className="adm-textarea" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} placeholder="Escribí el mensaje…" /></label>
        <label className="adm-field"><span>Audiencia</span>
          <select className="adm-select" value={audience} onChange={(e) => setAudience(e.target.value)}>
            <option value="all">Todos los usuarios</option><option value="verified">Solo vendedores verificados</option>
          </select></label>
        <button className="adm-btn adm-btn-primary" style={{ marginTop: '12px' }} onClick={send} disabled={busy || !message.trim()}>
          {busy ? 'Enviando…' : 'Enviar difusión'}
        </button>
        {result && <div className="adm-resolve-msg">{result}</div>}
      </div>
    </div>
  );
}

// ── ACTIVIDAD (bitácora de auditoría) ─────────────────────────────────
function ActivityView() {
  const { data, error, loading } = useData('/api/admin/actions');
  if (loading) return <Loading />;
  if (error)   return <ErrorBox msg={error} />;
  return (
    <div>
      <h1 className="adm-title">Actividad (bitácora)</h1>
      <p className="adm-muted adm-hint">Cada acción de un empleado queda registrada — accountability del equipo de soporte.</p>
      <table className="adm-table">
        <thead><tr><th>Fecha</th><th>Empleado</th><th>Acción</th><th>Objetivo</th><th>Detalle</th></tr></thead>
        <tbody>
          {data.map((a) => (
            <tr key={a.id}>
              <td className="adm-muted">{fmtDate(a.createdAt)}</td>
              <td>@{a.admin?.username}</td>
              <td>{ACTION_LABELS[a.action] || a.action}</td>
              <td className="adm-muted">{a.targetType ? `${a.targetType} #${a.targetId}` : '—'}</td>
              <td className="adm-truncate">{a.meta || '—'}</td>
            </tr>
          ))}
          {data.length === 0 && <tr><td colSpan={5} className="adm-empty">Sin actividad registrada</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ── USUARIO 360 ───────────────────────────────────────────────────────
function UserDetailView({ userId, onBack, onOpenTrade }) {
  const { data, error, loading, reload } = useData(`/api/admin/users/${userId}`);
  const act = async (path, body) => {
    if (path === 'suspend' && !window.confirm('¿Suspender a este usuario?')) return;
    await adminFetch(`/api/admin/users/${userId}/${path}`, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
    reload();
  };
  const message = async () => { const t = window.prompt('Mensaje para el usuario:'); if (t && t.trim()) await act('message', { message: t.trim() }); };
  const addBond = async () => { const a = window.prompt('Monto del bono (ej. 100):'); if (a && parseFloat(a) > 0) await act('bond', { amount: parseFloat(a), currency: 'USDT' }); };

  if (loading) return <Loading />;
  if (error)   return <ErrorBox msg={error} />;
  const { user: u, trades, reviews, actions } = data;
  return (
    <div>
      <button className="adm-btn adm-btn-sm adm-back" onClick={onBack}>← Volver</button>
      <h1 className="adm-title">@{u.username}
        {u.verified && <span className="adm-badge ok">verificado</span>}
        {u.suspended && <span className="adm-badge st-CANCELLED">suspendido</span>}
        {u.isAdmin && <span className="adm-badge">admin</span>}
      </h1>

      <div className="adm-detail-actions">
        {u.verified ? <button className="adm-btn adm-btn-sm" onClick={() => act('unverify')}>Revocar verif.</button>
                    : <button className="adm-btn adm-btn-sm" onClick={() => act('verify')}>Verificar</button>}
        {u.suspended ? <button className="adm-btn adm-btn-sm" onClick={() => act('unsuspend')}>Reactivar</button>
                     : <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => act('suspend')}>Suspender</button>}
        <button className="adm-btn adm-btn-sm" onClick={message}>Mensaje</button>
        <button className="adm-btn adm-btn-sm" onClick={addBond}>+Bono</button>
      </div>

      <div className="adm-case-grid">
        <section className="adm-panel">
          <h3>Perfil</h3>
          <dl className="adm-dl">
            <dt>Nombre</dt><dd>{u.name}</dd>
            <dt>Email</dt><dd>{u.email}</dd>
            <dt>País</dt><dd>{u.country}</dd>
            <dt>Teléfono</dt><dd>{u.phone || '—'}</dd>
            <dt>KYC</dt><dd>Nivel {u.kycLevel || 0}</dd>
            <dt>Nivel/XP</dt><dd>L{u.level} · {fmtNum(u.xp)} XP</dd>
            <dt>Rating</dt><dd>⭐ {Number(u.averageRating || 0).toFixed(1)} ({u.totalReviews || 0})</dd>
            <dt>Trades</dt><dd>{u.trades} ({u.completedTrades || 0} compl.)</dd>
            <dt>Desde</dt><dd className="adm-muted">{fmtDate(u.createdAt)}</dd>
          </dl>
        </section>

        <section className="adm-panel">
          <h3>Bonos</h3>
          {(u.bonds || []).length === 0 ? <div className="adm-empty">Sin bonos</div> :
            u.bonds.map((b) => (
              <div key={b.id} className="adm-party-stats" style={{ padding: '4px 0' }}>
                <span className="adm-mono">{fmtNum(b.amount)} {b.currency}</span>{' '}
                <span className={`adm-badge ${b.status === 'ACTIVE' ? 'ok' : b.status === 'SLASHED' ? 'st-CANCELLED' : ''}`}>{BOND_ST[b.status] || b.status}</span>{' '}
                <span className="adm-muted">{fmtDate(b.createdAt)}</span>
              </div>
            ))}
        </section>

        <section className="adm-panel adm-chat-panel">
          <h3>Trades ({trades.length})</h3>
          <table className="adm-table">
            <thead><tr><th>ID</th><th>Rol</th><th>Estado</th><th>Monto</th><th>Fecha</th><th></th></tr></thead>
            <tbody>
              {trades.map((t) => (
                <tr key={t.id}>
                  <td>T-{t.id}</td>
                  <td>{t.buyerId === u.id ? 'comprador' : 'vendedor'}</td>
                  <td><span className={`adm-badge st-${t.status}`}>{STATUS[t.status] || t.status}</span></td>
                  <td className="adm-mono">{fmtNum(t.amount)} {t.crypto}</td>
                  <td className="adm-muted">{fmtDate(t.createdAt)}</td>
                  <td><button className="adm-btn adm-btn-sm" onClick={() => onOpenTrade(t.id)}>Ver</button></td>
                </tr>
              ))}
              {trades.length === 0 && <tr><td colSpan={6} className="adm-empty">Sin trades</td></tr>}
            </tbody>
          </table>
        </section>

        <section className="adm-panel">
          <h3>Reviews recibidas ({reviews.length})</h3>
          {reviews.length === 0 ? <div className="adm-empty">Sin reviews</div> :
            reviews.map((r) => (
              <div key={r.id} className="adm-msg">
                <span className="adm-msg-who">⭐ {r.rating}</span>
                <span className="adm-msg-time">de @{r.fromUser?.username} · {fmtDate(r.createdAt)}</span>
                {r.text && <div className="adm-msg-body">{r.text}</div>}
              </div>
            ))}
        </section>

        <section className="adm-panel">
          <h3>Acciones de soporte sobre este usuario ({actions.length})</h3>
          {actions.length === 0 ? <div className="adm-empty">Sin acciones</div> :
            actions.map((a) => (
              <div key={a.id} className="adm-party-stats" style={{ padding: '4px 0' }}>
                <b>{ACTION_LABELS[a.action] || a.action}</b> por @{a.admin?.username}{' '}
                <span className="adm-muted">{fmtDate(a.createdAt)}</span>{a.meta && <> · {a.meta}</>}
              </div>
            ))}
        </section>
      </div>
    </div>
  );
}

// ── helpers UI ────────────────────────────────────────────────────────
const Loading  = () => <div className="adm-loading">Cargando…</div>;
const ErrorBox = ({ msg }) => <div className="adm-errorbox">⚠️ {msg}</div>;
